import { partsOf } from "../../content/guides/guideModel.js";
import { localizedPath } from "../../content/pages/registry.js";
import { DEFAULT_LANGUAGE } from "../../lib/language.js";

/**
 * An internal href, resolved for the language this guide is being rendered in.
 * The same resolution `scripts/build-guides.mjs` applies, reading the same page
 * registry, so the two renderings of a guide cannot link to different places.
 */
const localizedHref = (href, language) => {
  const [path, fragment] = href.split("#");
  const resolved = localizedPath(path || "/", language);
  return fragment === undefined ? resolved : `${resolved}#${fragment}`;
};

/**
 * A guide, rendered from the content model.
 *
 * One of the two renderers — `scripts/build-guides.mjs` is the other, producing
 * the static document a crawler without JavaScript receives. Both read the same
 * model and emit the same elements with the same classes, which is what keeps a
 * guide from having two subtly different versions of itself.
 *
 * If you change the markup here, change it there. `guides.seo.test.mjs` fails
 * when the committed document is no longer what the generator produces, and
 * when either renderer stops handling a block type the other still emits.
 *
 * The markup is semantic first: `<article>` wrapping a `<header>` with the one
 * `<h1>`, then a `<section>` per part with its `<h2>`, `<h3>` for a sub-part,
 * `<ol>` where the order carries meaning and `<ul>` where it does not. The FAQ
 * is a definition list, always expanded — the same call the product pages made,
 * for the same reason: those answers are the most quotable passages on the page
 * and must not sit behind an interaction.
 *
 * Layout classes are the site's own `.wps-page` system, which every SEO page
 * already uses and which is responsive down to 479px. The `guide-` prefix is
 * only for what a marketing page genuinely does not have: article prose,
 * ordered steps, and the related-links block that closes a guide.
 *
 * Internal links are plain `<a href>`, not router links — exactly as the five
 * product pages do it, so the same anchor works in the static document and a
 * crawler sees one kind of link across the whole site.
 */

/** Inline content: a string, or string / { text, href } parts. */
const Inline = ({ value, language }) =>
  partsOf(value).map((part, i) =>
    typeof part === "string" ? (
      part
    ) : (
      <a key={i} className="wps-text-link" href={localizedHref(part.href, language)}>
        {part.text}
      </a>
    ),
  );

const Block = ({ block, language }) => {
  if (typeof block === "string") {
    return <p className="body-18">{block}</p>;
  }

  switch (block.type) {
    case "p":
      return (
        <p className="body-18">
          <Inline value={block.text} language={language} />
        </p>
      );

    case "subheading":
      return <h3 className="made-22 guide-subheading">{block.text}</h3>;

    case "list":
      return (
        <ul className="guide-list body-18">
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline value={item} language={language} />
            </li>
          ))}
        </ul>
      );

    case "steps":
      return (
        <ol className="guide-steps body-18">
          {block.items.map((item, i) => (
            <li key={i}>
              <Inline value={item} language={language} />
            </li>
          ))}
        </ol>
      );

    case "note":
      return (
        <p className="wps-note">
          <Inline value={block.text} language={language} />
        </p>
      );

    default:
      return null;
  }
};

const GuideArticle = ({ guide, relatedGuides = [], language = DEFAULT_LANGUAGE }) => (
  <article className="guide-article">
    <header className="section wps-hero guide-hero">
      <div className="container">
        {guide.eyebrow ? <p className="wps-eyebrow">{guide.eyebrow}</p> : null}
        <h1 className="heading-50 guide-title">{guide.title}</h1>
        {guide.intro.map((paragraph, i) => (
          <p key={i} className="body-20 guide-intro">
            {paragraph}
          </p>
        ))}
      </div>
    </header>

    {guide.hero ? (
      <section className="section topless wps-section guide-figure-section">
        <div className="container">
          <figure className="guide-figure">
            <img
              src={guide.hero.src}
              alt={guide.hero.alt}
              width={guide.hero.width}
              height={guide.hero.height}
              loading="lazy"
              decoding="async"
              className="wps-editorial-im"
            />
          </figure>
        </div>
      </section>
    ) : null}

    {guide.sections.map((section, si) => (
      <section key={si} className="section topless wps-section">
        <div className="container">
          <div className="wps-editorial wps-editorial-solo">
            <div className="wps-editorial-text guide-prose">
              <h2 className="heading-38">{section.heading}</h2>
              {section.content.map((block, bi) => (
                <Block key={bi} block={block} language={language} />
              ))}
            </div>
          </div>
        </div>
      </section>
    ))}

    {guide.faq?.length ? (
      <section className="section topless wps-section">
        <div className="container">
          <div className="wps-section-header">
            <h2 className="heading-38">{guide.faqHeading}</h2>
          </div>
          <dl className="wps-faq">
            {guide.faq.map((item, i) => (
              <div key={i} className="wps-faq-item">
                <dt className="made-22 wps-faq-q">{item.q}</dt>
                <dd className="wps-faq-a body-15">
                  <Inline value={item.a} language={language} />
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    ) : null}

    {guide.related ? (
      <section className="section topless wps-section">
        <div className="container">
          <div className="wps-section-header">
            <h2 className="heading-38">{guide.related.heading}</h2>
          </div>
          <ul className="guide-related">
            {guide.related.product.map((link) => (
              <li key={link.href} className="guide-related-item">
                <a
                  className="wps-text-link guide-related-link"
                  href={localizedHref(link.href, language)}
                >
                  {link.label}
                </a>
                <p className="body-15 guide-related-blurb">{link.blurb}</p>
              </li>
            ))}
            {/* `href` is resolved by the caller through `guideHref`, which is
                the one function that pairs a guide's slug with its prefix. A
                related guide with no version in this language keeps its English
                URL rather than being given an `/el/` address it does not have. */}
            {relatedGuides.map((related) => (
              <li key={related.href} className="guide-related-item">
                <a
                  className="wps-text-link guide-related-link"
                  href={related.href}
                >
                  {related.title}
                </a>
                <p className="body-15 guide-related-blurb">
                  {related.metaDescription}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>
    ) : null}
  </article>
);

export default GuideArticle;
